import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface ReadinessGateProps {
  /** Minimum time in milliseconds the gate stays open before `onComplete` can fire. */
  minDisplayMs: number;
  /**
   * External readiness flag. `onComplete` fires only when BOTH the
   * `minDisplayMs` timer has elapsed AND `canComplete` is `true`.
   *
   * When `true`, the gate completes as soon as `minDisplayMs` elapses.
   * When `false`, the gate holds indefinitely until flipped to `true`.
   */
  canComplete: boolean;
  /** Called exactly once when both conditions are met (guarded by an internal ref). */
  onComplete: () => void;
  children: ReactNode;
}

/**
 * Readiness gate component — `max(minDisplayMs, canComplete)` unmount pattern.
 *
 * Renders `children` unconditionally (pass-through) and fires `onComplete`
 * exactly once when **both** conditions are satisfied:
 *
 * 1. `minDisplayMs` milliseconds have elapsed since mount.
 * 2. `canComplete` is `true`.
 *
 * Whichever condition is met last triggers the callback.
 *
 * **Exactly-once guarantee:** An internal ref prevents `onComplete` from
 * firing more than once per mount, even if the parent re-renders with a new
 * `onComplete` identity or toggles `canComplete` back and forth.
 *
 * **Indefinite-gate mode:** If `canComplete` remains `false` permanently,
 * `onComplete` will never fire and the component stays in its gated state.
 * This is intentional — consumers that need a "wait forever until ready"
 * semantic can rely on this behavior.
 *
 * @see _bmad-output/planning-artifacts/architecture-patterns.md — Pattern 2
 */
export function ReadinessGate({
  minDisplayMs,
  canComplete,
  onComplete,
  children,
}: ReadinessGateProps) {
  const [timerElapsed, setTimerElapsed] = useState(false);
  const firedRef = useRef(false);

  // Effect 1: minimum display timer — starts once on mount, cleans up on
  // unmount (essential for React 19 StrictMode double-invocation in dev).
  // Dep array is [] by design: minDisplayMs is captured at mount time and
  // treated as immutable (Pattern 2). Changing it mid-life requires a
  // remount via a key prop.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setTimerElapsed(true);
    }, minDisplayMs);
    return () => {
      window.clearTimeout(id);
    };
  }, []);

  // Effect 2: gate — fire onComplete when BOTH conditions are met, exactly once.
  useEffect(() => {
    if (timerElapsed && canComplete && !firedRef.current) {
      firedRef.current = true;
      onComplete();
    }
  }, [timerElapsed, canComplete, onComplete]);

  return <>{children}</>;
}
